import { FinancialUnavailable, isCurrentAssessment, type FinancialResponseAvailability, type FinancialNumeric } from '../financial-unavailable';
/**
 * DSR Analysis Panel (SPRINT-6B F003)
 *
 * Displays/manages Debt Service Ratio (DSR) calculations for deals.
 * Shows:
 * - Summary card with DSCR ratio, DSR %, status badge, and surplus income
 * - Income breakdown (gross, shading %, assessable)
 * - Expense breakdown (living, operating, other commitments)
 * - Debt service breakdown (existing, proposed, total)
 * - Calculate button for backend recalculation
 *
 * States:
 * - Empty: No calculations exist, show "Calculate DSR" button
 * - Display: Show DSR summary with breakdowns
 * - Calculating: Loading state during calculation API call
 */

import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Calculator, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';

// Types
interface DsrCalculation extends FinancialResponseAvailability {
  id: string;
  dealId: string;
  financialPeriodId: string | null;
  calculationDate: string;
  periodLabel: string;
  grossIncome: FinancialNumeric;
  incomeShadingApplied: FinancialNumeric;
  assessableIncome: FinancialNumeric;
  livingExpenses: FinancialNumeric;
  hemBenchmark: FinancialNumeric;
  operatingExpenses: FinancialNumeric;
  otherCommitments: FinancialNumeric;
  existingDebtService: FinancialNumeric;
  proposedDebtService: FinancialNumeric;
  totalDebtService: FinancialNumeric;
  surplusIncome: FinancialNumeric;
  dsrPercentage: FinancialNumeric;
  dscrRatio: FinancialNumeric;
  dsrStatus: 'strong' | 'adequate' | 'marginal' | 'weak' | null;
  lenderMaxDsr: FinancialNumeric;
  lenderMinDscr: FinancialNumeric;
  meetsRequirement: boolean | null;
  trendDirection: 'improving' | 'stable' | 'declining' | null;
  calculationNotes: string | null;
  createdAt: string;
  updatedAt: string;
}

interface CalculationResult extends FinancialResponseAvailability {
  dscrRatio: FinancialNumeric;
  dsrPercentage: FinancialNumeric;
  dsrStatus: 'strong' | 'adequate' | 'marginal' | 'weak' | null;
  surplusIncome: FinancialNumeric;
  meetsRequirement: boolean | null;
  breakdown: {
    income: {
      gross: FinancialNumeric;
      shadingPercent: FinancialNumeric;
      assessable: FinancialNumeric;
    };
    expenses: {
      living: FinancialNumeric;
      operating: FinancialNumeric;
      otherCommitments: FinancialNumeric;
    };
    debtService: {
      existing: FinancialNumeric;
      proposed: FinancialNumeric;
      total: FinancialNumeric;
    };
  };
}

export interface DsrAnalysisPanelProps {
  dealId: string;
}

// Status badge colour mapping
const STATUS_VARIANTS: Record<string, 'default' | 'secondary' | 'outline' | 'destructive'> = {
  strong: 'default',      // Green
  adequate: 'secondary',  // Blue
  marginal: 'outline',    // Amber/warning
  weak: 'destructive',    // Red
};

const STATUS_LABELS: Record<string, string> = {
  strong: 'Strong',
  adequate: 'Adequate',
  marginal: 'Marginal',
  weak: 'Weak',
};

// Format currency
function formatCurrency(value: FinancialNumeric): string {
  if (value == null) return 'Not assessed';
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(value));
}

// Format ratio with 2 decimal places
function formatRatio(value: FinancialNumeric): string {
  if (value == null) return 'Not assessed';
  return Number(value).toFixed(2);
}

// Format percentage with 1 decimal place
function formatPercentage(value: FinancialNumeric): string {
  if (value == null) return 'Not assessed';
  return Number(value).toFixed(1);
}

const ScopedDsrAnalysisPanel: React.FC<DsrAnalysisPanelProps> = ({ dealId }) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [calculationResult, setCalculationResult] = React.useState<CalculationResult | null>(null);

  // Fetch DSR calculations for the deal
  const { data: calculations = [], isLoading, error } = useQuery<DsrCalculation[]>({
    queryKey: [`/api/crm/deals/${dealId}/dsr-calculations`],
    enabled: !!dealId,
  });

  // Get most recent calculation or use calculation result
  const latestCalculation = calculations.length > 0 ? calculations[calculations.length - 1] : null;

  // Calculate mutation
  const calculateMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest('POST', '/api/financial/calculate-dsr', {
        dealId,
      });
      return res.json() as Promise<CalculationResult>;
    },
    onSuccess: (data) => {
      setCalculationResult(data);
      queryClient.invalidateQueries({ queryKey: [`/api/crm/deals/${dealId}/dsr-calculations`] });
      toast({
        title: isCurrentAssessment(data) ? 'DSR calculated' : 'Not assessed',
        description: !isCurrentAssessment(data) ? 'Add an approved financial basis before calculation.' : `DSCR: ${formatRatio(data.dscrRatio)}x, DSR: ${formatPercentage(data.dsrPercentage)}% (${STATUS_LABELS[data.dsrStatus ?? ""]})`,
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to calculate DSR',
        variant: 'destructive',
      });
    },
  });

  // Get current DSR data (from calculation result or latest record)
  const currentDsr = calculationResult || (latestCalculation ? {
    dscrRatio: latestCalculation.dscrRatio,
    dsrPercentage: latestCalculation.dsrPercentage,
    dsrStatus: latestCalculation.dsrStatus,
    surplusIncome: latestCalculation.surplusIncome,
    meetsRequirement: latestCalculation.meetsRequirement,
    breakdown: {
      income: {
        gross: latestCalculation.grossIncome,
        shadingPercent: latestCalculation.incomeShadingApplied,
        assessable: latestCalculation.assessableIncome,
      },
      expenses: {
        living: latestCalculation.livingExpenses,
        operating: latestCalculation.operatingExpenses,
        otherCommitments: latestCalculation.otherCommitments,
      },
      debtService: {
        existing: latestCalculation.existingDebtService,
        proposed: latestCalculation.proposedDebtService,
        total: latestCalculation.totalDebtService,
      },
    },
  } : null);

  // Loading state
  if (isLoading) {
    return (
      <Card data-testid="dsr-analysis-panel">
        <CardHeader className="pb-3">
          <Skeleton className="h-5 w-48" />
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i}>
                <Skeleton className="h-4 w-24 mb-2" />
                <Skeleton className="h-5 w-32" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Error state
  if (error) {
    return (
      <Card data-testid="dsr-analysis-panel">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold">DSR Analysis</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Error loading DSR data</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="dsr-analysis-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold">DSR Analysis</CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={() => calculateMutation.mutate()}
            disabled={calculateMutation.isPending}
          >
            {calculateMutation.isPending ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Calculator className="h-4 w-4 mr-1" />
            )}
            Calculate
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {calculateMutation.isError || !isCurrentAssessment(calculationResult ?? latestCalculation) ? (<FinancialUnavailable dealId={dealId} value={calculateMutation.isError ? { assessmentStatus: 'failed', historical: latestCalculation } : calculationResult ?? latestCalculation} action="Add income sources and commitments, then calculate." />) : !currentDsr ? (
          // Empty state
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <p className="text-sm text-muted-foreground mb-4">No DSR calculations available</p>
            <p className="text-xs text-muted-foreground mb-6">
              Add income sources and commitments, then calculate
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Summary Card */}
            <div className="flex items-start justify-between p-4 bg-muted/50 rounded-lg">
              <div>
                <p className="text-xs text-muted-foreground mb-1">DSCR</p>
                <div className="flex items-center gap-2">
                  <span
                    className="text-2xl font-bold"
                    data-testid="dsr-ratio-value"
                  >
                    {formatRatio(currentDsr.dscrRatio)}x
                  </span>
                </div>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">DSR %</p>
                <div className="flex items-center gap-2">
                  <span
                    className="text-2xl font-bold"
                    data-testid="dsr-percentage-value"
                  >
                    {formatPercentage(currentDsr.dsrPercentage)}%
                  </span>
                  <Badge
                    variant={STATUS_VARIANTS[currentDsr.dsrStatus ?? ""]}
                    data-testid="dsr-status-badge"
                  >
                    {STATUS_LABELS[currentDsr.dsrStatus ?? ""]}
                  </Badge>
                </div>
              </div>
            </div>

            {/* Surplus Income */}
            <dl className="grid grid-cols-1 gap-3 text-sm">
              <div>
                <dt className="text-muted-foreground">Surplus Income</dt>
                <dd
                  className="font-medium text-foreground"
                  data-testid="surplus-income-value"
                >
                  {formatCurrency(currentDsr.surplusIncome)}
                </dd>
              </div>
            </dl>

            {/* Income Breakdown */}
            <div className="border-t pt-4" data-testid="income-breakdown">
              <h4 className="text-xs font-semibold text-muted-foreground mb-2">INCOME</h4>
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Gross Income</dt>
                  <dd className="font-medium">{formatCurrency(currentDsr.breakdown.income.gross)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Shading Applied</dt>
                  <dd className="font-medium">{formatPercentage(currentDsr.breakdown.income.shadingPercent)}%</dd>
                </div>
                <div className="flex justify-between border-t pt-2">
                  <dt className="font-semibold">Assessable Income</dt>
                  <dd className="font-bold">{formatCurrency(currentDsr.breakdown.income.assessable)}</dd>
                </div>
              </dl>
            </div>

            {/* Expense Breakdown */}
            <div className="border-t pt-4" data-testid="expense-breakdown">
              <h4 className="text-xs font-semibold text-muted-foreground mb-2">EXPENSES</h4>
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Living Expenses</dt>
                  <dd className="font-medium">{formatCurrency(currentDsr.breakdown.expenses.living)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Operating Expenses</dt>
                  <dd className="font-medium">{formatCurrency(currentDsr.breakdown.expenses.operating)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Other Commitments</dt>
                  <dd className="font-medium">{formatCurrency(currentDsr.breakdown.expenses.otherCommitments)}</dd>
                </div>
              </dl>
            </div>

            {/* Debt Service Breakdown */}
            <div className="border-t pt-4" data-testid="debt-service-breakdown">
              <h4 className="text-xs font-semibold text-muted-foreground mb-2">DEBT SERVICE</h4>
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Existing Debt Service</dt>
                  <dd className="font-medium">{formatCurrency(currentDsr.breakdown.debtService.existing)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Proposed Debt Service</dt>
                  <dd className="font-medium">{formatCurrency(currentDsr.breakdown.debtService.proposed)}</dd>
                </div>
                <div className="flex justify-between border-t pt-2">
                  <dt className="font-semibold">Total Debt Service</dt>
                  <dd className="font-bold">{formatCurrency(currentDsr.breakdown.debtService.total)}</dd>
                </div>
              </dl>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const DsrAnalysisPanel: React.FC<DsrAnalysisPanelProps> = props => <ScopedDsrAnalysisPanel key={props.dealId} {...props} />;

export default DsrAnalysisPanel;
